import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MoreVertical, Archive, ArchiveRestore, Trash2, MessageSquarePlus, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StagePill } from "@/components/FictionalBadge";
import { toast } from "sonner";
import api from "@/lib/api";
function timeAgo(iso) {
  const d = new Date(iso); const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Label({ kind }) {
  const real = kind === "real";
  return <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider ${real ? "bg-[#12281a] text-[#9FE0B0]" : "bg-[#24201C] text-[#D4AF37]"}`}>{real ? "Real person" : "AI persona"}</span>;
}

export default function Chats() {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [ch, cn] = await Promise.all([api.get("/chats"), api.get("/connections").catch(() => ({ data: [] }))]);
      setChats(ch.data); setConnections(cn.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doArchive = async (id, archive) => { await api.post(`/chats/${id}/${archive ? "archive" : "unarchive"}`); load(); };
  const doDelete = async (id, name) => { await api.post(`/chats/${id}/delete`, { delete_memories: false }); toast.success(`Conversation with ${name} deleted`); load(); };

  const active = chats.filter((c) => !c.archived);
  const showAI = filter === "all" || filter === "ai";
  const showReal = filter === "all" || filter === "real";
  const empty = active.length === 0 && connections.length === 0;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8" data-testid="chats-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-3xl font-medium tracking-tight text-[#F4F1EA] md:text-4xl">Chats</h1>
        <Button data-testid="chats-discover-btn" onClick={() => navigate("/app/home")} className="rounded-full bg-[#D4AF37] font-semibold text-[#0A0908] hover:bg-[#E5C158]">New</Button>
      </div>

      <div className="mb-4 flex gap-2">
        {[["all", "All"], ["real", "Real people"], ["ai", "AI personas"]].map(([k, l]) => (
          <button key={k} data-testid={`chats-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-4 py-1.5 text-sm ${filter === k ? "border-[#D4AF37] bg-[#24201C] text-[#D4AF37]" : "border-[#3D3428] text-[#C8C2B5]"}`}>{l}</button>
        ))}
      </div>

      {loading ? <p className="text-sm text-[#8C8578]">Loading…</p> : empty ? (
        <div className="mt-16 flex flex-col items-center text-center" data-testid="chats-empty">
          <MessageSquarePlus className="mb-4 h-10 w-10 text-[#3D3428]" />
          <p className="font-display text-2xl text-[#F4F1EA]">No conversations yet</p>
          <p className="mt-2 max-w-xs text-sm text-[#8C8578]">Meet someone real or an AI persona from Home.</p>
          <Button onClick={() => navigate("/app/home")} className="mt-6 rounded-full bg-[#D4AF37] font-semibold text-[#0A0908]">Go to Home</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {showReal && connections.map((c) => (
            <motion.button key={c.match_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate(`/app/connection/${c.match_id}`)}
              data-testid={`connection-row-${c.match_id}`} className="flex w-full items-center gap-3 rounded-2xl border border-[#2B241B] bg-[#12100E] p-3 text-left">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#1f4a30] bg-[#12281a] text-[#9FE0B0]"><Users className="h-6 w-6" /></div>
              <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="truncate font-display text-lg text-[#F4F1EA]">{c.alias}</span></div>
                <p className="truncate text-sm text-[#8C8578]">Saved connection</p>
                <div className="mt-1 flex items-center gap-2"><Label kind="real" /><span className="text-[10px] text-[#8C8578]">{c.language}</span></div></div>
            </motion.button>
          ))}

          {showAI && active.map((c) => (
            <motion.div key={c.character.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 rounded-2xl border border-[#2B241B] bg-[#12100E] p-3" data-testid={`chat-row-${c.character.id}`}>
              <button className="flex flex-1 items-center gap-3 text-left" onClick={() => navigate(`/app/chat/${c.character.id}`)}>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#6B5720] bg-[#211B12] font-display text-lg text-[#D4AF37]">{c.character.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}</div>
                <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="truncate font-display text-lg text-[#F4F1EA]">{c.character.name}</span>{c.unread && <span className="h-2 w-2 rounded-full bg-[#D4AF37]" />}</div>
                  <p className="truncate text-sm text-[#8C8578]">{c.preview || "Say hello…"}</p>
                  <div className="mt-1 flex items-center gap-2"><Label kind="ai" /><StagePill stage={c.stage} /><span className="text-[10px] text-[#8C8578]">{c.language}</span></div></div>
              </button>
              <div className="flex flex-col items-end gap-2"><span className="text-[10px] text-[#8C8578]">{timeAgo(c.last_active)}</span>
                <DropdownMenu><DropdownMenuTrigger asChild><button data-testid={`chat-menu-${c.character.id}`} className="rounded-lg p-1.5 text-[#8C8578] hover:bg-[#1A1714]"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
                  <DropdownMenuContent className="border-[#3D3428] bg-[#12100E] text-[#C8C2B5]">
                    <DropdownMenuItem onClick={() => doArchive(c.character.id, true)} className="gap-2"><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`delete-${c.character.id}`} onClick={() => doDelete(c.character.id, c.character.name)} className="gap-2 text-[#c98a8a]"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu></div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
